import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { AuthService, RedirectedFromUrlKey } from '../services/auth.service';
import { environment } from '../../environments/environment';

@Injectable()
export class AuthGuard  {
  constructor(
    private router: Router,
    private auth: AuthService,
  ) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.authGuard(state.url);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    return this.authGuard(state.url);
  }

  authGuard(redirectedFromUrl: string): Observable<boolean | UrlTree> {
    if (this.auth.preservingLegacySession) {
      if (this.auth.hasValidLegacySession) return of(true);
      this.auth.resumeWithOidc(redirectedFromUrl);
      return of(false);
    }
    if (this.auth.shouldUseOidc(redirectedFromUrl)) {
      return from(this.auth.oidc.ensureAuthenticated(redirectedFromUrl));
    }
    
    if (this.auth.expires === null) {
      this.auth.tryLogoutMoEngage();
      this.auth.ThirdPartyServicesAuthenticated = false;

      localStorage.setItem(RedirectedFromUrlKey, redirectedFromUrl);

      this.router.navigate(['/login']);

      return of(false);
    }

    const tokenExpiration: Date = new Date(this.auth.expires);
    const now: Date = new Date();
    const timeDeviation: number = environment.loginExprirationDeviation;
    const deviatedTokenExpiration = new Date(tokenExpiration.valueOf() - timeDeviation);

    if (this.auth.token !== null && this.auth.refToken !== null) {
      if (now < tokenExpiration) {
        if (now > deviatedTokenExpiration) {
          return this.auth.refreshToken(this.auth.refToken).pipe(
            map((response) => {
              const responseJson = response.json();

              this.auth.pipeLogin(responseJson.access_token, responseJson.refresh_token, responseJson['.expires']);

              return true;
            }),
            catchError(() => {
              this.auth.logout();

              localStorage.setItem(RedirectedFromUrlKey, redirectedFromUrl);
              this.router.navigate(['/login']);

              return of(false);
            })
          );
        }

        return of(true);
      }
    }

    this.auth.logout();

    localStorage.setItem(RedirectedFromUrlKey, redirectedFromUrl);
    this.router.navigate(['/login']);

    return of(false);
  }
}
